import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { PriorityChecklist } from "@/components/PriorityChecklist";
import { BATHROOM_POST as P, MAIN_SITE, formatDate, isPublished, postUrl, track } from "@/lib/blog";
import { SITE_NAME, SITE_URL, LOGO_URL } from "@/lib/site";

const URL = postUrl(P);

const TOC = [
  ["decide", "1. Decide what you want your bathroom to do better"],
  ["layout", "2. Review the layout and storage together"],
  ["shower", "3. Think through your bathtub or shower priorities"],
  ["budget", "4. Set a working budget around a clear scope"],
  ["finishes", "5. Compare finishes with everyday use in mind"],
  ["ventilation", "6. Include ventilation and existing conditions in the plan"],
  ["checklist", "7. Build your bathroom priority checklist"],
  ["conversation", "8. Prepare for the first project conversation"],
  ["questions", "Common bathroom planning questions"],
  ["contact", "Discuss your bathroom project with Dog Day Renovations"],
] as const;

export const Route = createFileRoute("/blog/planning-bathroom-remodel-winston-salem")({
  head: () => {
    const live = isPublished(P);
    return {
      meta: [
        { title: P.seoTitle },
        { name: "description", content: P.description },
        { property: "og:title", content: P.seoTitle },
        { property: "og:description", content: P.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { property: "article:published_time", content: P.publishedAt },
        { name: "twitter:card", content: "summary" },
        ...(live ? [] : [{ name: "robots", content: "noindex" }]),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BlogPosting",
                headline: P.title,
                description: P.description,
                datePublished: P.publishedAt,
                dateModified: P.publishedAt,
                mainEntityOfPage: URL,
                url: URL,
                author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
                publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: LOGO_URL } },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                  { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
                  { "@type": "ListItem", position: 3, name: P.title, item: URL },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: Article,
});

const H2 = ({ id, children }: { id: string; children: ReactNode }) => (
  <h2 id={id} className="mt-12 scroll-mt-28 font-display text-2xl text-navy sm:text-3xl">{children}</h2>
);
const H3 = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-8 text-xl font-bold text-navy">{children}</h3>
);
const Ul = ({ children }: { children: ReactNode }) => (
  <ul className="my-5 list-disc space-y-2 pl-6">{children}</ul>
);
const A = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} className="font-semibold text-primary-ink underline underline-offset-2">{children}</a>
);

const contactCta = (where: string) => () =>
  track("article_contact_click", { article_slug: P.slug, interaction_type: where });
const phoneCta = () => track("article_phone_click", { article_slug: P.slug, interaction_type: "tel" });

function Article() {
  const live = isPublished(P);
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <ol className="flex flex-wrap gap-2">
            <li><Link to="/" className="hover:text-primary-ink">Home</Link> /</li>
            <li><Link to="/blog" className="hover:text-primary-ink">Blog</Link> /</li>
            <li aria-current="page" className="text-navy">Planning a Bathroom Remodel</li>
          </ol>
        </nav>

        <header className="relative mt-6 overflow-hidden rounded-3xl bg-navy px-6 py-12 text-navy-foreground sm:px-12 sm:py-16">
          <div aria-hidden className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
          <div aria-hidden className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-primary-glow/20 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-primary-glow">{P.category}</div>
            <h1 className="mt-3 font-display text-4xl leading-tight sm:text-5xl">{P.title}</h1>
            <p className="mt-4 text-lg opacity-80">{P.excerpt}</p>
            <p className="mt-6 text-sm opacity-70">
              By {P.byline} · {live ? "" : "Scheduled for "}
              <time dateTime={P.publishedAt}>{formatDate(P.publishedAt)}</time> · {P.readingMinutes} min read
            </p>
          </div>
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-[16rem_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav aria-label="Table of contents" className="rounded-xl border border-border p-5">
              <div className="font-display text-sm uppercase tracking-widest text-navy">In this guide</div>
              <ol className="mt-3 space-y-2 text-sm">
                {TOC.map(([id, label]) => (
                  <li key={id}><a href={`#${id}`} className="text-muted-foreground hover:text-primary-ink">{label}</a></li>
                ))}
              </ol>
            </nav>
          </aside>

          <article className="max-w-[70ch] text-base leading-relaxed text-foreground sm:text-lg [&_p]:my-5">
            <p>A bathroom can look dated and still work well. It can also look attractive while making every morning harder: drawers collide, towels have nowhere to go, or the shower no longer suits the people using it.</p>
            <p>If you are planning a bathroom remodel in Winston-Salem, begin with the changes that would make the room work better. Identify your priorities, review the layout, establish a project scope, and then compare materials and finishes. Those decisions give you a useful starting point for a contractor conversation.</p>
            <p>You do not need every detail figured out before asking for help. This guide will help you organize the questions that matter.</p>

            <H2 id="decide">1. Decide what you want your bathroom to do better</H2>
            <p>Before saving more inspiration photos, spend a few minutes thinking about an ordinary day in your bathroom.</p>
            <Ul>
              <li>Who uses the room, and does anyone need to use it at the same time?</li>
              <li>Where do you feel crowded or short on storage?</li>
              <li>Which surfaces or fixtures are difficult to clean or use?</li>
              <li>What works well enough that you would be happy to keep it?</li>
            </Ul>
            <p>Write down your three most important improvements. Then make a separate list of changes that would be nice to have.</p>
            <p>For example, better storage and a more practical shower might be essential, while decorative wall tile could be optional. This distinction gives you a way to make decisions if the first version of your wish list exceeds your preferred budget.</p>

            <H2 id="layout">2. Review the layout and storage together</H2>
            <p>A larger vanity is only helpful if you can still open its drawers and move comfortably around it. Think about the relationship between the door, toilet, vanity, and bathing area before choosing individual products.</p>
            <p>Look at where the door swings, how you reach towels, and whether two people can move through the room comfortably. Take photos from the doorway and each corner. Rough measurements can help explain your ideas, but final product sizes should follow verified measurements.</p>
            <p>Ask your contractor to compare keeping the current fixture locations with changing the layout. Moving fixtures can involve additional plumbing and construction work. Keeping them in place may simplify the scope, but it should still solve the problems that prompted the remodel.</p>
            <p>For storage, make a quick inventory of what actually needs a home: towels, toiletries, cleaning supplies, and everyday grooming items. Plan around that list instead of choosing cabinetry by appearance alone.</p>

            <H2 id="shower">3. Think through your bathtub or shower priorities</H2>
            <p>Consider how your household uses the bathing area now and how those needs could change.</p>
            <p>Do you regularly use the bathtub? Would a different shower arrangement improve your routine? Where should shower products go? Are reachable controls, a seat, or easier entry important topics to discuss?</p>
            <p>Bring these preferences to the planning conversation before choosing a shower enclosure or ordering materials. A contractor can assess what fits the room and what preparation the project requires.</p>
            <p>If the shower is a central part of your project, explore Dog Day Renovations’ <A href={`${MAIN_SITE}/tile-shower-installation`}>tile and shower installation services</A> as you develop your wish list.</p>

            <H2 id="budget">4. Set a working budget around a clear scope</H2>
            <p>A bathroom budget becomes more useful when everyone understands what the project includes. A finish update, fixture replacement, and a layout change can involve different work even when the rooms are similar in size.</p>
            <p>Tell your contractor your preferred spending range and which priorities matter most. Ask for a written scope that explains what is included, what is excluded, and which selections remain undecided.</p>
            <p>Useful questions include:</p>
            <Ul>
              <li>Does the estimate include removal, preparation, installation, and cleanup?</li>
              <li>Which fixtures and materials are included, and who purchases them?</li>
              <li>How will unexpected conditions be documented and discussed?</li>
              <li>How will changes to the agreed work be priced and approved?</li>
            </Ul>
            <p>Discuss an allowance for surprises appropriate to the project. A budget should help you make informed choices, rather than depend on a generic online price that may describe a very different bathroom.</p>

            <H2 id="finishes">5. Compare finishes with everyday use in mind</H2>
            <p>Photos are a good way to explain the look you like. Samples and product information help you decide whether that look fits your routine.</p>
            <p>For each material, ask about cleaning, upkeep, and suitability for its intended location. A tile selected for a wall may not be appropriate for a shower floor. Confirm the product’s intended use and installation requirements with the contractor before buying it.</p>
            <p>Consider lighting, too. A finish can look different in a showroom than it does in your bathroom. View samples together under the room’s lighting when possible.</p>
            <p>Narrow your choices to a coordinated set of finishes. Making those selections early gives your contractor something concrete to check for compatibility and availability.</p>

            <H2 id="ventilation">6. Include ventilation and existing conditions in the plan</H2>
            <p>Moisture management deserves attention alongside the visible finishes. Ask your contractor to assess existing leaks, ventilation, and the preparation needed in wet areas.</p>
            <p>The <A href="https://www.epa.gov/indoor-air-quality-iaq/remodeling-your-home-and-indoor-air-quality">U.S. Environmental Protection Agency’s bathroom remodeling guidance</A> recommends checking that the bathroom exhaust fan works effectively and discharges outdoors. A fan making noise does not, by itself, confirm that it is removing enough air.</p>
            <p>Raise concerns such as recurring dampness or visible damage during the initial visit so they can be considered in the scope.</p>

            <H2 id="checklist">7. Build your bathroom priority checklist</H2>
            <p>Select the improvements that matter to you. Use your selections as conversation starters when discussing your bathroom project.</p>
            <PriorityChecklist title={P.title} url={URL} slug={P.slug} />
            <p>Your selections are a planning aid. The next step is to discuss what is practical for your space, priorities, and budget.</p>
            <p><a href={`${MAIN_SITE}/contact-us`} onClick={contactCta("inline")} className="font-semibold text-primary-ink underline">Discuss Your Bathroom Project →</a></p>

            <H2 id="conversation">8. Prepare for the first project conversation</H2>
            <p>Gather a few photos of the existing bathroom, your priority list, and two or three inspiration images. Add notes explaining what you like about each image: the storage, colors, layout, or shower arrangement.</p>
            <p>Share your preferred timing and any household constraints. If this is your only bathroom, ask how access will be managed during the work. Discuss material availability, the likely sequence of work, and when a project-specific schedule can be confirmed.</p>
            <p>A simple project brief could read:</p>
            <blockquote className="my-6 border-l-4 border-primary bg-cream px-5 py-4 italic text-navy">“We want better towel storage, less clutter around the sink, and a shower that is easier to use. We are open to keeping the current layout and would like to compare options before choosing finishes.”</blockquote>
            <p>That is enough to begin a useful conversation. You can learn more about the service on our <A href={`${MAIN_SITE}/bathroom-remodeling`}>bathroom remodeling page</A>.</p>

            <H2 id="questions">Common bathroom planning questions</H2>
            <H3>Do I need to choose all my materials before contacting a contractor?</H3>
            <p>No. Start with your goals, preferred spending range, and examples of what you like. Discuss the scope and measurements before committing to products, then agree on when final selections need to be made.</p>
            <H3>Can I remodel a bathroom without changing the layout?</H3>
            <p>Yes, a project can focus on fixtures, storage, and finishes while retaining the existing arrangement. Whether that approach fits your goals depends on the room’s condition and the improvements you want to make.</p>
            <H3>How long should I allow for the remodel?</H3>
            <p>Ask for a schedule based on your particular project. Planning, product availability, the amount of work, and conditions found during preparation can affect timing. Clarify both the expected start date and how long the bathroom may be unavailable.</p>

            <section className="mt-12 rounded-2xl bg-navy p-8 text-navy-foreground">
              <h2 id="contact" className="scroll-mt-28 font-display text-2xl sm:text-3xl">Discuss your bathroom project with Dog Day Renovations</h2>
              <p className="opacity-85">You can start with a few priorities and a room that needs to work better. Dog Day Renovations offers bathroom remodeling and tile and shower installation. Tell us what you would like to change and where you are in the planning process.</p>
              <a href={`${MAIN_SITE}/contact-us`} onClick={contactCta("final_cta")} className="mt-2 inline-flex min-h-12 items-center rounded-md bg-gradient-primary px-6 py-3 font-bold uppercase tracking-wide text-navy">Discuss Your Bathroom Project</a>
              <p className="opacity-85">Prefer to call? Reach Dog Day Renovations at <a href="tel:+13368934447" onClick={phoneCta} className="font-bold text-primary-glow underline">(336) 893-4447</a>.</p>
            </section>
          </article>
        </div>
      </main>
    </div>
  );
}
